import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { MaskLine, Label } from "./Motion";
import { HERO_IMG } from "../data";

export const Hero = ({ scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Warm luxury interior" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2c2826]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c2826]/70 via-transparent to-[#2c2826]/30" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <Label className="text-[#e7ddcf]">Home Decor &amp; Curated Gifting</Label>
        </motion.div>

        <h1 className="mt-5 font-serif text-[#F7F5F0] text-[13vw] leading-[0.92] md:text-[8.5vw] lg:text-[7rem] tracking-tight">
          <MaskLine delay={0.35}>Luxury</MaskLine>
          <MaskLine delay={0.5} className="italic text-[#E8942E]">lives here.</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-[#e7ddcf] text-base md:text-lg leading-relaxed"
          >
            Cushions, candles, vases and curated hampers — objects with a story,
            made for the moments that make a house a home.
          </motion.p>

          <motion.button
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            onClick={() => scrollTo("collection")}
            className="group inline-flex items-center gap-3 self-start rounded-full border border-[#F7F5F0]/40 bg-[#F7F5F0]/5 backdrop-blur-sm text-[#F7F5F0] pl-6 pr-3 py-3 hover:bg-[#F7F5F0] hover:text-[#2c2826] transition-colors duration-400"
          >
            Explore the collection
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[#F7F5F0] text-[#2c2826] group-hover:bg-[#985F47] group-hover:text-white transition-colors duration-400">
              <ArrowDown size={16} />
            </span>
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};
